import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import Button from 'components/Button';
import * as React from 'react';
import { tenantConfig } from 'utils/tenant';
import { IVehicle } from 'data/schema';
import { userSession } from 'state/session';
import { classNames } from 'utils/ui/css';
import { getComparisonVehicle } from 'state/session/compare';

const Wltp: React.FC = () => {
  const { _ } = useLingui();
  const [isWltpTruncated, setIsWltpTruncated] = React.useState(false);
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const wltpRef = React.useRef<HTMLDivElement>(null);
  const session = userSession().useState();
  const comparisonVehicle = getComparisonVehicle();
  const [, stashedVehicle] = session.comparisonMode === 'on' ? comparisonVehicle : [undefined, undefined];
  const vehicles = [userSession().currentVehicle, stashedVehicle].filter(Boolean) as IVehicle[];

  tenantConfig().useState();
  const showWltp = tenantConfig().isAllowed('wltp');

  const checkWltpTruncated = () => {
    const contentElements = wltpRef.current?.querySelectorAll('[data-testid="wltp-text"]');

    const overflowing = Array.from(contentElements || []).some((el) => el.scrollWidth > el.clientWidth);
    setIsWltpTruncated(overflowing);
  };

  /** Adjust on wltpText change */
  React.useEffect(() => {
    checkWltpTruncated();
  }, [session.wltpText]);

  /** Adjust on window resize and register the event only once */
  React.useEffect(() => {
    window.addEventListener('resize', checkWltpTruncated);
    return () => window.removeEventListener('resize', checkWltpTruncated);
  }, []);

  if (!showWltp) {
    // Return empty div to make the layout stable independent of WLTP being shown or not
    return <div />;
  }

  return (
    <>
      <div className="ex3d-wltp" ref={wltpRef}>
        <div data-testid="wltp-container" className="line-clamp-2 overflow-hidden">
          {vehicles.map((vehicle, index) => (
            <div key={index} data-testid="wltp-content" className="flex" part="wltp">
              {vehicles.length > 1 && !!vehicle.wltpText && (
                <span
                  className={classNames(
                    "mr-2 font-['TheGroupTEXTBold'] font-semibold block whitespace-nowrap",
                    isWltpTruncated && 'truncate max-w-24 min-w-12'
                  )}
                >
                  {vehicle.carDisplayName}:
                </span>
              )}
              <span data-testid="wltp-text" className="block truncate whitespace-nowrap">
                {vehicle.wltpText}
              </span>
            </div>
          ))}
        </div>
      </div>

      {isWltpTruncated && (
        <div className="flex h-full justify-center">
          <button
            className="ml-2 mr-4 whitespace-nowrap font-['TheGroupTEXTBold'] text-xs"
            onClick={() => setIsModalVisible(true)}
          >
            <Trans>Show more</Trans>
          </button>
        </div>
      )}

      {isModalVisible && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/50 text-black transition-opacity">
          <div className="w-1/2 rounded-lg bg-white p-10 shadow-lg max-lg:w-2/3">
            <h3 className="text-2xl font-semibold capitalize">
              <Trans>WLTP disclaimer</Trans>
            </h3>
            {vehicles.map((vehicle, index) => {
              return (
                <div key={index}>
                  {/* Show vehicle name if we are comparing and this vehicle has WLTP text */}
                  {vehicles.length > 1 && !!vehicle.wltpText && (
                    <h4 className="mb-2 mt-4 text-xl font-semibold">{vehicle.carDisplayName}</h4>
                  )}
                  <p className="my-4">{vehicle.wltpText}</p>
                </div>
              );
            })}
            <div className="flex justify-end">
              <Button text={_(t`Close`)} onClick={() => setIsModalVisible(false)} className="ex3d-button--primary" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Wltp;
